export type Lang = 'en' | 'es';

const SCRIPT_ID = 'google-translate-script';
const CONTAINER_ID = 'google_translate_element';

declare global {
  interface Window {
    googleTranslateElementInit?: () => void;
    google?: {
      translate?: {
        TranslateElement: new (options: Record<string, unknown>, elementId: string) => unknown;
      };
    };
  }
}

/** Reads the current language from Google Translate's cookie. */
export function getLang(): Lang {
  return /googtrans=\/en\/es/.test(document.cookie) ? 'es' : 'en';
}

function writeCookie(value: string, expires?: string) {
  const host = window.location.hostname;
  const exp = expires ? `; expires=${expires}` : '';
  document.cookie = `googtrans=${value}; path=/${exp}`;
  if (host.includes('.')) {
    document.cookie = `googtrans=${value}; path=/; domain=${host}${exp}`;
    document.cookie = `googtrans=${value}; path=/; domain=.${host}${exp}`;
  }
}

/**
 * Google Translate wraps text in <font> tags, which can make React crash when it later
 * updates that text. These guards make React tolerant of those DOM changes.
 */
function patchDomForTranslation() {
  const proto = Node.prototype as Node & { __translatePatched?: boolean };
  if (proto.__translatePatched) return;
  proto.__translatePatched = true;

  const originalRemoveChild = Node.prototype.removeChild;
  Node.prototype.removeChild = function <T extends Node>(this: Node, child: T): T {
    if (child.parentNode !== this) {
      return child;
    }
    return originalRemoveChild.call(this, child) as T;
  };

  const originalInsertBefore = Node.prototype.insertBefore;
  Node.prototype.insertBefore = function <T extends Node>(this: Node, newNode: T, ref: Node | null): T {
    if (ref && ref.parentNode !== this) {
      return newNode;
    }
    return originalInsertBefore.call(this, newNode, ref) as T;
  };
}

/** Loads the Google Translate engine once, hidden. Call on app start. */
export function initTranslation() {
  patchDomForTranslation();
  if (document.getElementById(SCRIPT_ID)) return;

  const container = document.createElement('div');
  container.id = CONTAINER_ID;
  container.style.display = 'none';
  document.body.appendChild(container);

  window.googleTranslateElementInit = () => {
    if (!window.google?.translate) return;
    new window.google.translate.TranslateElement(
      { pageLanguage: 'en', includedLanguages: 'en,es', autoDisplay: false },
      CONTAINER_ID
    );
  };

  const script = document.createElement('script');
  script.id = SCRIPT_ID;
  script.src = 'https://translate.google.com/translate_a/element.js?cb=googleTranslateElementInit';
  script.async = true;
  document.body.appendChild(script);
}

/** Switches the whole site's language. */
export function setLang(lang: Lang) {
  if (lang === 'es') {
    writeCookie('/en/es');
    const combo = document.querySelector<HTMLSelectElement>('.goog-te-combo');
    if (combo) {
      combo.value = 'es';
      combo.dispatchEvent(new Event('change'));
    } else {
      window.location.reload();
    }
  } else {
    writeCookie('', 'Thu, 01 Jan 1970 00:00:00 GMT');
    window.location.reload();
  }
}
